import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { getContent, PassphraseLocale } from '../../lib/passphraseContent'

interface PassphraseEducationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locale: PassphraseLocale;
}

/**
 * PassphraseEducationModal - Minimal monochrome education modal
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 */
export default function PassphraseEducationModal({
  isOpen,
  onClose,
  locale,
}: PassphraseEducationModalProps) {
  const content = getContent(locale);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Manage focus trap and body scroll lock
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        modalRef.current?.querySelector('h2')?.focus();
      }, 100);
    } else {
      document.body.style.overflow = '';

      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
  };

  const transition = {
    duration: 0.2,
    ease: 'easeOut',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={transition}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Dialog */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              ref={modalRef}
              className="relative w-full max-w-2xl max-h-[90vh] bg-neutral-950 border border-white/10 overflow-hidden flex flex-col"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={transition}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h2
                  id="modal-title"
                  className="text-xl font-extralight text-white focus:outline-none"
                  tabIndex={-1}
                >
                  {content.modal.title}
                </h2>
                <button
                  onClick={onClose}
                  className="text-white/30 hover:text-white/60 transition-colors p-1"
                  aria-label="Close dialog"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="space-y-8">
                  {/* Intro Section */}
                  <section className="space-y-4">
                    <p className="text-white/60 font-light leading-relaxed">
                      {content.modal.intro}
                    </p>
                    {/* Warning Box */}
                    <div className="border-l-2 border-white/40 pl-4 py-2">
                      <p className="text-white/50 text-sm font-light">
                        {content.modal.neverStore}
                      </p>
                    </div>
                  </section>

                  {/* Why Passphrase Section */}
                  <section className="space-y-3">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.whyTitle}
                    </h3>
                    <p className="text-white/60 font-light leading-relaxed">
                      {content.modal.whyContent}
                    </p>
                  </section>

                  {/* How to Create Section */}
                  <section className="space-y-4">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.howTitle}
                    </h3>
                    <p className="text-white/60 font-light leading-relaxed">
                      {content.modal.howContent}
                    </p>
                    <div className="p-4 border border-white/10">
                      <p className="text-xs text-white/30 uppercase tracking-widest mb-2">
                        {content.modal.formatLabel}
                      </p>
                      <code className="text-sm text-white/70 font-mono">
                        {content.modal.formatExample}
                      </code>
                    </div>
                  </section>

                  {/* Where NOT to Store Section */}
                  <section className="space-y-3">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.dontStoreTitle}
                    </h3>
                    <ul className="space-y-2">
                      {content.modal.dontStoreList.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="text-white/30 text-xs mt-1">✕</span>
                          <span className="text-white/50 font-light text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Where TO Store Section */}
                  <section className="space-y-3">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.doStoreTitle}
                    </h3>
                    <ul className="space-y-2">
                      {content.modal.doStoreList.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="text-white/50 text-xs mt-1">✓</span>
                          <span className="text-white/60 font-light text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Recovery Options Section */}
                  <section className="space-y-4">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.recoveryTitle}
                    </h3>
                    <div className="space-y-3">
                      {content.modal.recoveryTiers.map((tier, index) => (
                        <div
                          key={index}
                          className="p-4 border border-white/10"
                        >
                          <h4 className="text-white/70 font-light mb-2">
                            {tier.title}
                          </h4>
                          <p className="text-white/40 text-sm font-light leading-relaxed">
                            {tier.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Best Practices Section */}
                  <section className="space-y-3">
                    <h3 className="text-sm text-white/30 uppercase tracking-widest">
                      {content.modal.bestPracticesTitle}
                    </h3>
                    <ul className="space-y-2">
                      {content.modal.bestPracticesList.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="text-white/30 text-xs mt-1">•</span>
                          <span className="text-white/50 font-light text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Security Notice Section */}
                  <section>
                    <div className="border border-white/20 p-4">
                      <h4 className="text-white/70 font-light mb-2">
                        {content.modal.securityNoticeTitle}
                      </h4>
                      <p className="text-white/40 text-sm font-light">
                        {content.modal.securityNoticeContent}
                      </p>
                    </div>
                  </section>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10">
                <button
                  onClick={onClose}
                  className="w-full bg-white text-black text-sm font-medium py-3 px-6
                           hover:bg-white/90 transition-colors"
                >
                  {content.modal.closeButton}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
